import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Modal, Button } from 'react-bootstrap';
import { Icon } from '@iconify/react/dist/iconify.js';
import { toast, ToastContainer } from 'react-toastify';
import Skeleton from 'react-loading-skeleton';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { LoginContext } from '../AuthContext/LoginContext';
import { PRIORITY_META, PRIORITIES } from '../utils/priority';
import 'react-toastify/dist/ReactToastify.css';
import 'react-loading-skeleton/dist/skeleton.css';

// Full CRUD for "Nature of Complaints". Each nature carries a priority; admins /
// CRO Lead drag natures between the four priority columns to (re)assign priority.
//   GET   /backend/complaint-natures/get-all-complaint-natures
//   POST  /backend/complaint-natures/create-complaint-nature   (admin/CRO/CRO Lead)
//   PATCH /backend/complaint-natures/:id                        (admin/CRO/CRO Lead)
//   PATCH /backend/complaint-natures/delete/:id                 (admin/CRO/CRO Lead)
const ComplaintNatureCrudLayer = () => {
  const { user } = useContext(LoginContext);
  const API_BASE_URL = process.env.REACT_APP_BE_API_URL;
  const role = String(user?.role || '').toLowerCase();
  const canEditOrDelete = role === 'admin' || role === 'cro' || role === 'cro_lead';
  const authHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
  });

  const [natures, setNatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [nameInput, setNameInput] = useState('');
  const [priorityInput, setPriorityInput] = useState('');
  const [saving, setSaving] = useState(false);

  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchNatures = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/backend/complaint-natures/get-all-complaint-natures`,
        { params: { perPage: 1000, pageNo: 1 }, ...authHeader() }
      );
      setNatures(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      if (err.response?.status === 404) setNatures([]);
      else {
        console.error('Error fetching complaint natures:', err);
        toast.error('Failed to load complaint natures.');
      }
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => { fetchNatures(); }, [fetchNatures]);

  // Group filtered natures by priority for the columns.
  const term = search.trim().toLowerCase();
  const columns = useMemo(() => {
    const groups = { Critical: [], High: [], Medium: [], Low: [] };
    natures
      .filter((n) => !term || String(n.name || '').toLowerCase().includes(term))
      .forEach((n) => {
        const p = PRIORITIES.includes(n.priority_level) ? n.priority_level : 'Medium';
        groups[p].push(n);
      });
    return groups;
  }, [natures, term]);

  const openAdd = () => { setEditing(null); setNameInput(''); setPriorityInput(''); setShowForm(true); };
  const openEdit = (n) => { setEditing(n); setNameInput(n.name || ''); setPriorityInput(n.priority_level || ''); setShowForm(true); };
  const closeForm = () => { if (!saving) { setShowForm(false); setEditing(null); setNameInput(''); setPriorityInput(''); } };

  const handleSave = async (e) => {
    e.preventDefault();
    const name = nameInput.trim();
    if (!name) { toast.error('Please enter a name.'); return; }
    if (!priorityInput) { toast.error('Please select a Priority Level.'); return; }
    setSaving(true);
    try {
      const payload = { name, priority_level: priorityInput };
      if (editing) {
        await axios.patch(`${API_BASE_URL}/backend/complaint-natures/${editing._id}`, payload, authHeader());
        toast.success('Complaint nature updated.');
      } else {
        await axios.post(`${API_BASE_URL}/backend/complaint-natures/create-complaint-nature`, payload, authHeader());
        toast.success('Complaint nature added.');
      }
      closeForm();
      fetchNatures();
    } catch (err) {
      console.error('Error saving complaint nature:', err);
      toast.error(err.response?.data?.message || 'Failed to save. Admin access is required.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await axios.patch(`${API_BASE_URL}/backend/complaint-natures/delete/${toDelete._id}`, {}, authHeader());
      toast.success('Complaint nature deleted.');
      setToDelete(null);
      fetchNatures();
    } catch (err) {
      console.error('Error deleting complaint nature:', err);
      toast.error(err.response?.data?.message || 'Failed to delete. Admin access is required.');
    } finally {
      setDeleting(false);
    }
  };

  // Drag a nature card into another priority column -> persist the new priority.
  const onDragEnd = async (result) => {
    if (!canEditOrDelete) return;

    const { draggableId, source, destination } = result;
    if (!destination || destination.droppableId === source.droppableId) return;
    const newPriority = destination.droppableId;
    const prev = natures;
    // Optimistic update
    setNatures((list) => list.map((n) => (n._id === draggableId ? { ...n, priority_level: newPriority } : n)));
    try {
      await axios.patch(
        `${API_BASE_URL}/backend/complaint-natures/${draggableId}`,
        { priority_level: newPriority },
        authHeader()
      );
      toast.success(`Moved to ${newPriority}.`);
    } catch (err) {
      console.error('Error updating priority:', err);
      toast.error(err.response?.data?.message || 'Failed to update priority. Admin access is required.');
      setNatures(prev); // revert
    }
  };

  const totalShown = PRIORITIES.reduce((s, p) => s + columns[p].length, 0);

  return (
    <div className="card h-100 p-0 radius-12">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="card-header border-bottom bg-base py-16 px-24 d-flex flex-wrap align-items-center justify-content-between gap-3">
        <div>
          <h5 className="fw-bold mb-1 d-flex align-items-center gap-2">
            <Icon icon="mdi:shape-outline" className="text-primary-600" /> Nature of Complaints
          </h5>
          <span className="text-secondary-light text-sm">{canEditOrDelete ? 'Drag a nature between the priority columns to set its priority. ' : ''}Complaint priority is taken from its nature.</span>
        </div>
        <button type="button" className="btn btn-primary btn-sm d-flex align-items-center gap-2" onClick={openAdd}>
          <Icon icon="mdi:plus" style={{ fontSize: 18 }} /> Add Nature
        </button>
      </div>

      <div className="card-body p-24">
        <div className="row mb-3">
          <div className="col-xl-4 col-lg-6 col-md-8 col-12">
            <div className="position-relative">
              <Icon
                icon="mdi:magnify"
                className="position-absolute top-50 translate-middle-y text-muted"
                style={{ left: '14px', fontSize: '20px', pointerEvents: 'none' }}
              />
              <input
                type="search"
                className="form-control ps-5 py-2"
                placeholder="Search by name"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoComplete="off"
              />
            </div>
          </div>
          <div className="col d-flex align-items-center justify-content-end">
            <span className="text-secondary-light text-sm">{totalShown} nature{totalShown === 1 ? '' : 's'}</span>
          </div>
        </div>

        {loading ? (
          <div className="row g-3">
            {PRIORITIES.map((p) => (
              <div className="col-xl-3 col-md-6" key={p}><Skeleton height={320} /></div>
            ))}
          </div>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <div className="row g-3">
              {PRIORITIES.map((p) => {
                const meta = PRIORITY_META[p];
                return (
                  <div className="col-xl-3 col-md-6" key={p}>
                    <div className="radius-12 h-100" style={{ background: meta.bg, border: `1px solid ${meta.border}` }}>
                      <div
                        className="d-flex align-items-center justify-content-between px-16 py-12"
                        style={{ borderBottom: `1px solid ${meta.border}` }}
                      >
                        <span className="d-flex align-items-center gap-2 fw-bold" style={{ color: meta.color }}>
                          <Icon icon={meta.icon} /> {p}
                        </span>
                        <span className="badge" style={{ background: meta.color, color: '#fff' }}>{columns[p].length}</span>
                      </div>
                      <Droppable droppableId={p}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className="px-12 py-12"
                            style={{ minHeight: 240, background: snapshot.isDraggingOver ? `${meta.color}14` : 'transparent', transition: 'background .15s' }}
                          >
                            {columns[p].length === 0 && (
                              <div className="text-center text-secondary-light py-4" style={{ fontSize: 12.5 }}>
                                Drop natures here
                              </div>
                            )}
                            {columns[p].map((n, index) => (
                              <Draggable draggableId={n._id} index={index} key={n._id} isDragDisabled={!canEditOrDelete}>
                                {(dp, ds) => (
                                  <div
                                    ref={dp.innerRef}
                                    {...dp.draggableProps}
                                    {...dp.dragHandleProps}
                                    className="d-flex align-items-center justify-content-between gap-2 bg-white radius-8 px-12 py-10 mb-8"
                                    style={{
                                      border: '1px solid #eef2f6',
                                      boxShadow: ds.isDragging ? '0 8px 20px rgba(15,23,42,.15)' : '0 1px 2px rgba(15,23,42,.05)',
                                      ...dp.draggableProps.style,
                                    }}
                                  >
                                    <span className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                                      {canEditOrDelete && <Icon icon="mdi:drag" className="text-secondary-light flex-shrink-0" />}
                                      <span className="fw-medium text-truncate" title={n.name}>{n.name}</span>
                                    </span>
                                    {canEditOrDelete && (
                                      <span className="d-flex align-items-center gap-1 flex-shrink-0">
                                        <button
                                          type="button"
                                          className="w-28-px h-28-px bg-primary-light text-primary-600 rounded-circle d-flex justify-content-center align-items-center"
                                          title="Edit"
                                          onClick={() => openEdit(n)}
                                        >
                                          <Icon icon="lucide:edit" style={{ fontSize: 14 }} />
                                        </button>
                                        <button
                                          type="button"
                                          className="w-28-px h-28-px bg-danger-light text-danger-600 rounded-circle d-flex justify-content-center align-items-center"
                                          title="Delete"
                                          onClick={() => setToDelete(n)}
                                        >
                                          <Icon icon="mingcute:delete-2-line" style={{ fontSize: 14 }} />
                                        </button>
                                      </span>
                                    )}
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </div>
                  </div>
                );
              })}
            </div>
          </DragDropContext>
        )}
      </div>

      {/* Add / Edit modal */}
      <Modal show={showForm} onHide={closeForm} centered>
        <form onSubmit={handleSave}>
          <Modal.Header closeButton>
            <Modal.Title className="d-flex align-items-center gap-2" style={{ fontSize: 18 }}>
              <Icon icon={editing ? 'lucide:edit' : 'mdi:plus-circle-outline'} />
              {editing ? 'Edit Complaint Nature' : 'Add Complaint Nature'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <label className="form-label fw-medium">Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g., Illegal Tree Cutting"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                autoFocus
              />
            </div>
            <div>
              <label className="form-label fw-medium">Priority Level <span className="text-danger">*</span></label>
              <select
                className="form-select"
                value={priorityInput}
                onChange={(e) => setPriorityInput(e.target.value)}
                required
              >
                <option value="">Select Priority Level</option>
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              {priorityInput && (
                <span
                  className="badge mt-2"
                  style={{ background: PRIORITY_META[priorityInput].color, color: '#fff' }}
                >
                  {priorityInput}
                </span>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeForm} disabled={saving}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? (
                <span className="d-flex align-items-center gap-2">
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  Saving…
                </span>
              ) : (editing ? 'Update' : 'Add')}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Delete confirm modal */}
      <Modal show={!!toDelete} onHide={() => !deleting && setToDelete(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 18 }}>Delete Complaint Nature</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete <strong>{toDelete?.name}</strong>? This will remove it from the selectable list.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setToDelete(null)} disabled={deleting}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ComplaintNatureCrudLayer;
